/**
 * Page access by persona (plans/00 5.3; artifact `parts/90-v4-core.js` ACCESS, v4.2).
 * Mirrored on the backend in Settings `access_matrix`, which enforces the same
 * matrix on every `dr` endpoint, so a hidden page is never only hidden.
 *
 * Auto Intelligence (policies, insights, graph, hub) is read-only for every
 * persona except `policy-owner` and `supervity-support` (TDD D25).
 */

import type { PersonaId } from '@/lib/persona'

/** Artifact page ids, kept as the nav item ids. */
export type PageId =
  | 'ask'
  | 'console'
  | 'timeline'
  | 'globe'
  | 'agencycalls'
  | 'checks'
  | 'workbench'
  | 'depots'
  | 'deliveries'
  | 'receipt'
  | 'checkin'
  | 'policies'
  | 'insights'
  | 'graph'
  | 'hub'
  | 'audit'
  | 'data'
  | 'presenter'
  | 'access'

const STORY: readonly PageId[] = ['ask', 'console', 'timeline', 'globe', 'agencycalls', 'checks', 'workbench']
const READ: readonly PageId[] = ['policies', 'insights', 'graph', 'hub']

export const ACCESS: Readonly<Record<PersonaId, readonly PageId[] | '*'>> = {
  'hq-chief': [...STORY, 'depots', 'deliveries', 'audit', ...READ],
  'supplies-desk': [...STORY, 'depots', 'deliveries', 'receipt', 'checkin', 'audit', ...READ],
  'hq-secretariat': [...STORY, 'depots', 'deliveries', 'receipt', 'checkin', 'audit', ...READ],
  'liaison-staff': [...STORY, 'deliveries', 'checkin', 'audit', 'policies', 'graph'],
  'kantei-pr': [...STORY, 'policies'],
  'policy-owner': ['ask', 'console', 'timeline', 'globe', 'workbench', 'audit', ...READ],
  'supervity-support': '*',
}

export function canSee(persona: PersonaId, pageId: PageId): boolean {
  const pages = ACCESS[persona]
  if (pages === '*') return true
  return (pages ?? []).includes(pageId)
}

/** Editors of Auto Policies, insights and the AutoGraph (plans/06 0.2); everyone else reads. */
export function canEditAutoIntelligence(persona: PersonaId): boolean {
  return persona === 'policy-owner' || persona === 'supervity-support'
}

/**
 * The page a path belongs to, for guards and the active rail item.
 * Case records and destination detail belong to the Auto Workbench, the
 * shelter check-in form to Impact Checks' `checkin`, receipts to `receipt`.
 */
export function pageIdForPath(pathname: string | null | undefined): PageId | null {
  if (!pathname) return null
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/') return 'console'
  const first = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`)
  if (first('/ask')) return 'ask'
  if (first('/timeline')) return 'timeline'
  if (first('/globe')) return 'globe'
  if (first('/agencies')) return 'agencycalls'
  if (first('/checks')) return 'checks'
  if (first('/checkin')) return 'checkin'
  if (first('/workbench')) return 'workbench'
  if (first('/supplies')) return 'depots'
  if (first('/deliveries')) return 'deliveries'
  if (first('/receipt')) return 'receipt'
  if (first('/ai/policies')) return 'policies'
  if (first('/ai/insights')) return 'insights'
  if (first('/ai/graph')) return 'graph'
  if (first('/ai-coworker-hub')) return 'hub'
  if (first('/audit')) return 'audit'
  if (first('/data-manager')) return 'data'
  if (first('/presenter')) return 'presenter'
  if (first('/admin/access')) return 'access'
  return null
}
